/**
 * task-schedule-section.tsx — D11/A4 Time-block UI for a single task.
 *
 * Shows either:
 *  - A linked event (already scheduled): date+time summary + "Mở lịch" link.
 *    Duplicate-guard: no second form is shown when event already linked.
 *  - An inline form (date + time picker) to create a new calendar event.
 *
 * Color for new events is derived from task priority via PRIO map.
 */
import { useState } from "react";
import { CalendarDays } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { Task } from "../types";
import { BentoDateInput, BentoTimeInput, PRIO } from "./bento-ui";
import { todayIso } from "../lib/date";

type Props = {
  task: Task;
  api: LifeOsApi;
  /** Navigate to another entity (e.g. calendar view). Optional for back-compat. */
  openEntity?: (kind: string, id: string) => void;
};

/** Find the first event linked to this task (may be undefined). */
function useLinkedEvent(api: LifeOsApi, taskId: string) {
  return api.state.events.find((e) => e.linkedTaskId === taskId);
}

/** Format "YYYY-MM-DD HH:MM" as "DD/MM/YYYY HH:MM" for Vietnamese display. */
function fmtScheduled(date: string, time: string) {
  const d = new Date(`${date}T00:00:00`);
  const label = d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${label} ${time}`;
}

export function TaskScheduleSection({ task, api, openEntity }: Props) {
  const [date, setDate] = useState(task.dueDate || todayIso());
  const [time, setTime] = useState("09:00");

  const linked = useLinkedEvent(api, task.id);

  const field =
    "rounded-xl border border-ink-200 bg-white px-3 py-2 text-[13px] outline-none focus:border-brand-500";

  /** Create a new calendar event linked to this task. */
  const handleSchedule = () => {
    api.actions.addEvent({
      title: task.title,
      date,
      time,
      color: PRIO[task.priority] ?? "#7c3aed",
      durationMin: task.estimateMinutes > 0 ? task.estimateMinutes : 30,
      linkedTaskId: task.id,
    });
  };

  return (
    <div className="mt-6 border-t border-ink-200 pt-4">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-400">
        Lên lịch (Time-block)
      </p>

      {linked ? (
        /* Already scheduled — show summary + calendar link; no duplicate form */
        <div className="flex items-center gap-2.5 rounded-2xl bg-brand-50 px-3 py-2.5">
          <CalendarDays className="h-4 w-4 shrink-0 text-brand-500" />
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-brand-700">
              Đã lên lịch: {fmtScheduled(linked.date, linked.time)}
            </p>
            {linked.durationMin !== undefined && linked.durationMin > 0 && (
              <p className="text-[11px] text-ink-500">
                {linked.durationMin < 60
                  ? `${linked.durationMin} phút`
                  : `${Math.floor(linked.durationMin / 60)}h${linked.durationMin % 60 > 0 ? linked.durationMin % 60 + "p" : ""}`}
              </p>
            )}
          </div>
          {openEntity && (
            <button
              onClick={() => openEntity("event", linked.id)}
              className="shrink-0 rounded-lg bg-brand-100 px-3 py-1.5 text-[11px] font-bold text-brand-600 hover:bg-brand-200"
            >
              Mở lịch
            </button>
          )}
        </div>
      ) : (
        /* No linked event — show inline scheduling form */
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold text-ink-500">Ngày</span>
            <BentoDateInput
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={field}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold text-ink-500">Giờ</span>
            <BentoTimeInput
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className={field}
            />
          </label>
          <button
            onClick={handleSchedule}
            disabled={!date || !time}
            className="flex items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Lên lịch
          </button>
        </div>
      )}

      {!linked && task.estimateMinutes > 0 && (
        <p className="mt-1.5 text-[10px] text-ink-400">
          Thời lượng sẽ là {task.estimateMinutes} phút (từ ước tính của việc).
        </p>
      )}
    </div>
  );
}
